import { PoundCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { App, Button, Card, Skeleton, Tooltip } from 'antd';
import type { AffordabilityRunRecord, OwnerOccupiedAffordabilityRunOutput } from 'api/btlApplicationForm/types';
import { FormValidationContext, useOnNext } from 'hooks';
import { useBtlApplicationInProgressById, useGetAffordabilityCheck, useRunAffordabilityCheck } from 'queries';
import { STATUS_TO_CAN_RUN_AFFORDABILITY } from 'pages/BtlApplicationFormPage/constants';
import { useContext, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { CaseSidebarItemId } from 'components/CaseSidebar/types';
import { TypedFormatMessageFn } from 'types/intl';
import type { OoFormRouteContext } from '../types';
import { useOoFormMappedRoutes } from '../useOoFormMappedRoutes';

function getLatestOoAffordabilityOutput(
  runs: AffordabilityRunRecord[] | undefined,
): OwnerOccupiedAffordabilityRunOutput | undefined {
  if (!runs?.length) {
    return undefined;
  }

  const latestRun = [...runs].sort((runA, runB) => {
    const createdAtA = runA.createdAt ? new Date(runA.createdAt).getTime() : 0;
    const createdAtB = runB.createdAt ? new Date(runB.createdAt).getTime() : 0;

    return createdAtB - createdAtA;
  })[0];

  return latestRun?.ownerOccupiedOutput;
}

function formatOoAffordabilityFtv(
  financingAmount: number | undefined,
  propertyValue: number | undefined,
): string | undefined {
  if (financingAmount === undefined || propertyValue === undefined || propertyValue === 0) {
    return undefined;
  }

  return `${Math.round((financingAmount / propertyValue) * 100)}%`;
}

interface OoAffordabilityCheckProps {
  routeContext: OoFormRouteContext;
}

interface AffordabilityItemProps {
  label: string;
  value: string | undefined;
  valueClassName?: string;
  largeText?: boolean;
}

function AffordabilityItem({ label, value, valueClassName, largeText }: AffordabilityItemProps) {
  return (
    <div>
      <div>{label}</div>
      <div className={`${largeText ? 'text-2xl font-bold' : 'text-lg font-bold'} ${valueClassName ?? ''}`}>
        {value === undefined ? '---' : value}
      </div>
    </div>
  );
}

function formatCurrency(value: number | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(value);
}

function getAffordabilityResultDisplay(
  affordabilityData: OwnerOccupiedAffordabilityRunOutput | undefined,
  isAffordabilityRunRequired: boolean | undefined,
  formatMessage: TypedFormatMessageFn,
): { value: string | undefined; className: string } {
  if (affordabilityData === undefined) {
    return { value: undefined, className: '' };
  }

  if (isAffordabilityRunRequired) {
    return {
      value: formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.result.outdated' }),
      className: 'text-[#D97706]',
    };
  }

  if (affordabilityData.isPassed) {
    return {
      value: formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.result.passed' }),
      className: 'text-green-600',
    };
  }

  return {
    value: formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.result.failed' }),
    className: 'text-red-500',
  };
}

function OoAffordabilityResults({
  affordabilityData,
  propertyValue,
  isAffordabilityRunRequired,
  isLoading,
}: {
  affordabilityData: OwnerOccupiedAffordabilityRunOutput | undefined;
  propertyValue?: number;
  isAffordabilityRunRequired: boolean | undefined;
  isLoading: boolean;
}) {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const resultDisplay = getAffordabilityResultDisplay(affordabilityData, isAffordabilityRunRequired, formatMessage);

  return (
    <Skeleton loading={isLoading} active>
      <div className={`flex flex-col gap-4 ${affordabilityData ? '' : 'opacity-50'}`}>
        <div className={'flex gap-12'}>
          <AffordabilityItem
            label={formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.result.label' })}
            value={resultDisplay.value}
            valueClassName={resultDisplay.className}
            largeText
          />
          <AffordabilityItem
            label={formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.ftv.label' })}
            value={formatOoAffordabilityFtv(affordabilityData?.financingAmount, propertyValue)}
            largeText
          />
        </div>
        <AffordabilityItem
          label={formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.maxFinance.label' })}
          value={formatCurrency(affordabilityData?.maximumFinancingPossible)}
        />
        <AffordabilityItem
          label={formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.monthlyPayment.label' })}
          value={formatCurrency(affordabilityData?.initialMonthlyPayment)}
        />
      </div>
    </Skeleton>
  );
}

export function OoAffordabilityCheck({ routeContext }: OoAffordabilityCheckProps) {
  const { formatMessage }: { formatMessage: TypedFormatMessageFn } = useIntl();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const caseId = routeContext.caseId;
  const { refetch, isFetching } = useRunAffordabilityCheck(caseId);
  const { data: affordabilityRuns, isLoading: isRunsLoading } = useGetAffordabilityCheck(caseId);
  const { data: appData, isLoading: isAppLoading } = useBtlApplicationInProgressById(caseId);
  const { nextSection } = useOoFormMappedRoutes(routeContext);

  const formValidationContext = useContext(FormValidationContext);
  if (!formValidationContext) {
    throw new Error('OoAffordabilityCheck must be used within a FormValidationProvider');
  }
  const { setIsValid } = formValidationContext;

  const affordabilityData = getLatestOoAffordabilityOutput(affordabilityRuns);
  const hasProduct = Boolean(appData?.product);
  const canStatusRunAffordability =
    !appData?.status || STATUS_TO_CAN_RUN_AFFORDABILITY[appData.status as keyof typeof STATUS_TO_CAN_RUN_AFFORDABILITY];

  useEffect(() => {
    setIsValid(Boolean(affordabilityData?.isPassed && !isFetching && !appData?.isAffordabilityRunRequired));
  }, [affordabilityData, isFetching, appData?.isAffordabilityRunRequired, setIsValid]);

  const handleNavigate = (itemId: CaseSidebarItemId) => {
    navigate({ search: `?section=${itemId}` });
  };

  useOnNext(async () => {
    if (nextSection) {
      handleNavigate(nextSection);
    }
  });

  const handleRunAffordabilityCheck = async () => {
    try {
      await refetch();
      await queryClient.invalidateQueries({ queryKey: ['btl-application-get-affordability-check', caseId] });
      await queryClient.invalidateQueries({ queryKey: ['btl-application-in-progress', caseId] });
    } catch (error: unknown) {
      console.error(error);
      message.error(error instanceof Error ? error.message : 'Failed to run affordability check');
    }
  };

  return (
    <Card
      className={'w-fit'}
      loading={isFetching}
      actions={[
        <Tooltip
          key={'run'}
          title={!hasProduct ? formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.noProductSelected' }) : null}
          placement='top'
        >
          <span>
            <Button
              type={'link'}
              size={'large'}
              loading={isFetching}
              icon={<PoundCircleOutlined />}
              onClick={handleRunAffordabilityCheck}
              disabled={!canStatusRunAffordability || !hasProduct}
            >
              {formatMessage({ id: 'ooApplicationForm.AFFORDABILITY_CHECK.runButton' })}
            </Button>
          </span>
        </Tooltip>,
      ]}
    >
      <OoAffordabilityResults
        affordabilityData={affordabilityData}
        propertyValue={appData?.property?.purchasePrice}
        isAffordabilityRunRequired={appData?.isAffordabilityRunRequired}
        isLoading={isRunsLoading || isAppLoading}
      />
    </Card>
  );
}
